import { ApiError } from '../../../api/client.ts';
import { CHANNEL_LABELS, type DeliveryChannel } from '../../campaigns/compose/messagesDraft.ts';
import { humanizePath } from './localization.ts';

/** One entry of a 422 `translation_incomplete`: a locale marked ready that leaves text fields of a variant's channel empty. */
export interface IncompleteTranslation {
  variant: string;
  channel: string;
  locale: string;
  missing: string[];
}

/** The entries of a publish refused for incomplete translations (BB-55); `null` for any other error. */
export function incompleteTranslations(error: unknown): IncompleteTranslation[] | null {
  if (!(error instanceof ApiError) || error.code !== 'translation_incomplete') return null;
  const items = error.problem.incomplete;
  return Array.isArray(items) ? (items as IncompleteTranslation[]) : [];
}

/** Empty fields by locale for one variant's channel (or every entry without `scope`). */
export function incompleteByLocale(error: unknown, scope?: { variant: string; channel: string }): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const item of incompleteTranslations(error) ?? []) {
    if (scope && (item.variant !== scope.variant || item.channel !== scope.channel)) continue;
    out[item.locale] = [...new Set([...(out[item.locale] ?? []), ...item.missing])];
  }
  return out;
}

/**
 * Why publishing was refused when translations marked ready are incomplete (BB-55): variant, channel, locale and the
 * empty fields. Renders nothing for any other error, so it can sit next to a publish or save error.
 */
export function TranslationIncompleteNotice({ error, variantNames = {} }: { error: unknown; variantNames?: Readonly<Record<string, string>> }) {
  const items = incompleteTranslations(error);
  if (!items) return null;
  return (
    <div role="alert" className="notice notice--warning">
      <p>Not published: some translations marked ready have empty fields. Fill them, or set those locales back to draft.</p>
      {items.length > 0 && (
        <ul aria-label="Incomplete translations">
          {items.map((i) => (
            <li key={`${i.variant}/${i.channel}/${i.locale}`}>
              {variantNames[i.variant] ?? i.variant} · {CHANNEL_LABELS[i.channel as DeliveryChannel] ?? i.channel} · {i.locale}: {i.missing.map(humanizePath).join(', ')}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
